import { registerPlugin } from '@capacitor/core';

import type {
  BarcodeDocumentFormat,
  BarcodeDocumentParserResult,
  BarcodeItem,
  BarcodeMappedData,
  BarcodeScannerConfiguration,
  BarcodeScannerResult,
  BarcodeScannerScreenConfiguration,
  BarcodeScannerUiResult,
  BufferImageLoadOptions,
  DeepPartial,
  EncodeImageOptions,
  ImageInfo,
  ImageRefPoolSnapshot,
  LicenseInfo,
  PathImageLoadOptions,
  ResultWrapper,
  SaveImageOptions,
  SdkConfiguration,
  SerializedImageInput,
} from '../types';

type WrappedResult<T> = {
  result: T;
};
/**
 * @internal
 * @hidden
 */
export interface ScanbotBarcodeSDKImpl {
  initialize(configuration: SdkConfiguration): Promise<DeepPartial<LicenseInfo>>;

  getLicenseInfo(): Promise<DeepPartial<LicenseInfo>>;

  cleanupStorage(): Promise<void>;

  mockCamera(params: { imageFileUri: string; refreshOnEachFrame?: boolean }): Promise<void>;

  startBarcodeScanner(
    configuration: BarcodeScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<BarcodeScannerUiResult>>>;

  scanBarcodesFromImage(params: {
    image: SerializedImageInput;
    configuration: BarcodeScannerConfiguration;
  }): Promise<DeepPartial<BarcodeScannerResult>>;

  scanBarcodesFromPdf(params: {
    pdfFileUri: string;
    configuration: BarcodeScannerConfiguration;
  }): Promise<DeepPartial<BarcodeScannerResult>>;

  parseBarcodeDocument(params: {
    rawText: string;
    acceptedFormats?: BarcodeDocumentFormat[];
  }): Promise<DeepPartial<BarcodeDocumentParserResult>>;

  readImageData(params: { imageFileUri: string }): Promise<WrappedResult<string>>;

  imageRefSerialize(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  imageRefDeserialize(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  imageRefFromImageFileUri(params: { uri: string; options: PathImageLoadOptions }): Promise<WrappedResult<string>>;

  imageRefFromEncodedBuffer(params: {
    buffer: string;
    options: BufferImageLoadOptions;
  }): Promise<WrappedResult<string>>;

  imageRefHibernate(params: { uniqueId: string }): Promise<void>;

  imageRefClear(params: { uniqueId: string }): Promise<void>;

  imageRefInfo(params: { uniqueId: string }): Promise<DeepPartial<ImageInfo>>;

  imageRefSaveImage(params: {
    uniqueId: string;
    path: string;
    options: SaveImageOptions;
  }): Promise<WrappedResult<boolean>>;

  imageRefEncodeImage(params: { uniqueId: string; options: EncodeImageOptions }): Promise<WrappedResult<string>>;

  imageRefRelease(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  makeSnapshot(): Promise<DeepPartial<ImageRefPoolSnapshot>>;

  imageRefReleaseAll(): Promise<void>;

  /**
   * Helpers
   */

  // params are not used, but if we set the callback as first param, a deprecated warning is shown
  registerBarcodeItemMapperCallback(params: Record<string, never>, callback: (barcodeItem: BarcodeItem) => void): void;

  onBarcodeItemMapper(params: { barcodeItemUuid: string; barcodeMappedData?: BarcodeMappedData | null }): void;
}

/**
 * @internal
 * @hidden
 */
export const ScanbotBarcodeSDKPlugin = registerPlugin<ScanbotBarcodeSDKImpl>('ScanbotBarcodeSDK', {});
